import type { ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';

import { Button } from '#/components/Button/Button';
import type {
  ToastAction,
  ToastDismissedEventDetail,
  ToastIconName,
  ToastId,
  ToastVariant,
} from '#/types/ToastData';

import { ToastIcon } from './Icon/ToastIcon';
import { isToastIconName } from './Icon/isToastIconName';
import { toastIcons } from './Icon/toastIcons';
import {
  DismissButton,
  ToastActions,
  ToastActionsAndContent,
  ToastBody,
  ToastContent,
  ToastHeadline,
  ToastIconWrapper,
  ToastLayout,
  ToastWrapper,
  animationDurationMs,
} from './Toast.styled';
import { ToastFeedbackButton } from './ToastFeedbackButton';
import { useToastOnSwipeDown } from './useToastOnSwipeDown';

type Props = {
  toastId: ToastId;
  headline: string;
  variant: ToastVariant;
  layout: 'vertical' | 'compact';
  icon?: ToastIconName | Exclude<ReactNode, string>;
  body?: string | ReactNode;
  dismissible?: boolean;
  actions?: ToastAction[];
  onDismiss: () => void;
};

export function Toast({
  toastId,
  variant,
  headline,
  body,
  dismissible,
  layout,
  icon,
  actions,
  onDismiss,
}: Props) {
  const isMobile = 'ontouchstart' in window;

  const [isDismissing, setIsDismissing] = useState(false);
  const handleDismiss = useCallback(async () => {
    if (
      window.matchMedia?.('(prefers-reduced-motion: no-preference)')?.matches
    ) {
      setIsDismissing(true);
      await new Promise((resolve) => {
        window.setTimeout(resolve, animationDurationMs);
      });
    }

    onDismiss();
  }, [onDismiss]);

  useEffect(() => {
    const handleToastDismissed = (event: Event) => {
      const customEvent = event as CustomEvent<ToastDismissedEventDetail>;
      if (customEvent.detail.id !== toastId) return;
      handleDismiss();
    };
    window.addEventListener('idealist:toast-dismissed', handleToastDismissed);
    return () => {
      window.removeEventListener(
        'idealist:toast-dismissed',
        handleToastDismissed,
      );
    };
  }, [handleDismiss, toastId]);

  const { handlers } = useToastOnSwipeDown({ onSwipeDown: handleDismiss });
  const onSwipeHandlers = isMobile ? handlers : {};

  return (
    <ToastWrapper
      data-qa-id={`toast-${toastId || ''}`}
      $variant={variant}
      $isDismissing={isDismissing}
      $isMobile={isMobile}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...onSwipeHandlers}
    >
      <ToastLayout>
        {icon && (
          <ToastIconWrapper>
            {typeof icon === 'string' && isToastIconName(icon) ? (
              <ToastIcon
                name={icon}
                width={toastIcons[icon].width}
                height={toastIcons[icon].height}
              />
            ) : (
              icon
            )}
          </ToastIconWrapper>
        )}

        <ToastActionsAndContent $layout={layout}>
          <ToastContent>
            <ToastHeadline qaId="toast-headline">{headline}</ToastHeadline>

            {body && <ToastBody qaId="toast-body">{body}</ToastBody>}
          </ToastContent>

          <ToastActions data-qa-id="toast-actions">
            {actions?.map((action) =>
              action.type === 'button' ? (
                <Button
                  variant="primary"
                  type="button"
                  key={action.label}
                  qaId={`toast-action-${action.label.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={action.onClick}
                >
                  {action.label}
                </Button>
              ) : (
                <ToastFeedbackButton
                  key={action.id}
                  data-qa-id={`toast-feedback-${action.id}`}
                  onFeedback={action.onFeedback}
                  positiveLabel={action.positiveLabel}
                  negativeLabel={action.negativeLabel}
                />
              ),
            )}
          </ToastActions>
        </ToastActionsAndContent>
      </ToastLayout>

      {dismissible && (
        <DismissButton qaId="toast-dismiss-button" onClick={handleDismiss} />
      )}
    </ToastWrapper>
  );
}
